import React, {
  FC, useCallback, useEffect, useMemo,
} from 'react';
import { useTranslation } from 'next-i18next';
import isAfter from 'date-fns/isAfter';

import { filterCategoriesIds } from 'src/common/boatCategories';
import getCategoryKeyById from 'src/common/utils/getCategoryKeyById';
import SearchStore from 'src/search/store/SearchStore';
import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import { completePriceFormatter } from 'src/common/utils/currency';
import afterRenderCallback from 'src/common/utils/afterRenderCallback';
import { useSearchInputContext } from 'src/common/components/Search/SearchInputProvider';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import { formatDate } from 'src/common/utils/dateHelpers';
import ActiveFilterPill from './ActiveFilterPill';

export type ActiveFiltersProps = {
  store: SearchStore;
};

const ActiveFilters: FC<ActiveFiltersProps> = ({ store }) => {
  const locale = useDateFnsLocale();
  const [t] = useTranslation('search-page');
  const [tBoatCategories] = useTranslation('boat-categories');
  const [tSearch] = useTranslation('search-and-filters');
  const { currency } = useCurrencyContext();
  const {
    dates: { value: selectedDates },
    datesIsDirty,
    clearDates,
    capacity: { value: groupSize },
    capacityIsDirty,
    clearCapacity,
    min_price: { value: minPrice },
    max_price: { value: maxPrice },
    priceIsDirty,
    clearPrice,
    categories: {
      value: selectedCategories,
      onChange: onCategoriesChange,
    },
    categoriesIsDirty,
    captained: { value: captained },
    captainedIsDirty,
    clearCaptained,
    instabooks: { value: instabooks },
    instabooksIsDirty,
    clearInstabooks,
    superowners: { value: superowners },
    superownersIsDirty,
    clearSuperowners,
    durationIsDirty,
    clearDuration,
    hasFiltersApplied,
  } = store;

  const { addRecentSearchWithPrevious } = useSearchInputContext();

  const datesContent = useMemo((): string => {
    if (!selectedDates[0]) return null;
    const selectedDate = selectedDates[0];
    let alternateDateCount = 0;
    selectedDates.forEach((alternateDate) => {
      if (isAfter(alternateDate, selectedDate)) alternateDateCount += 1;
    });
    const alternateDateString = alternateDateCount
      ? `\u00A0± ${tSearch(`filter-range-buttons--option-${alternateDateCount > 1 ? 'plural' : 'single'}`, { count: alternateDateCount })}`
      : '';
    return `${formatDate(selectedDate, 'P', locale)} ${alternateDateString}`;
  }, [locale, selectedDates, tSearch]);

  const capacityContent = useMemo((): string => t('capacity-filter-size', { size: groupSize }), [groupSize, t]);

  const priceContent = useMemo((): string => {
    const minPriceFormatted = completePriceFormatter(minPrice, currency.code, currency.symbol);
    const maxPriceFormatted = completePriceFormatter(maxPrice, currency.code, currency.symbol);
    if (!maxPrice && minPrice) {
      return `${minPriceFormatted}+`;
    }
    return `${minPriceFormatted}-${maxPriceFormatted}`;
  }, [maxPrice, minPrice, currency.code, currency.symbol]);

  const superownersContent = useMemo((): string | null => superowners && tSearch('superowners-filter-chip-content'), [superowners, tSearch]);

  const captainedContent = useMemo((): string => {
    if (captained === 'true') {
      return tSearch('captain-filter-chip-content--provided');
    }
    if (captained === 'false') {
      return tSearch('captain-filter-chip-content--not-provided');
    }
    return null;
  }, [captained, tSearch]);

  const instabooksContent = useMemo((): string | null => instabooks && tSearch('instabook-filter-chip-content'), [instabooks, tSearch]);

  useEffect(() => {
    if (!hasFiltersApplied && durationIsDirty) clearDuration();
  }, [hasFiltersApplied, durationIsDirty, clearDuration]);

  const afterRender = useCallback(afterRenderCallback, []);

  return (
    <>
      {instabooksIsDirty && (
        <ActiveFilterPill
          clearAction={afterRender(() => {
            clearInstabooks();
            addRecentSearchWithPrevious({
              instabooks: null,
            });
          })}
          translationKey="instabooks"
        >
          {instabooksContent}
        </ActiveFilterPill>
      )}
      {superownersIsDirty && (
        <ActiveFilterPill
          clearAction={afterRender(() => {
            clearSuperowners();
            addRecentSearchWithPrevious({
              superowners: null,
            });
          })}
          translationKey="superowners"
        >
          {superownersContent}
        </ActiveFilterPill>
      )}
      {datesIsDirty && (
        <ActiveFilterPill
          clearAction={afterRender(() => {
            clearDates();
            addRecentSearchWithPrevious({
              dates: [],
            });
          })}
          translationKey="date"
        >
          {datesContent}
        </ActiveFilterPill>
      )}
      {capacityIsDirty && (
        <ActiveFilterPill
          clearAction={afterRender(() => {
            clearCapacity();
            addRecentSearchWithPrevious({
              capacity: null,
            });
          })}
          translationKey="capacity"
        >
          {capacityContent}
        </ActiveFilterPill>
      )}
      {priceIsDirty && (
        <ActiveFilterPill
          clearAction={afterRender(() => {
            clearPrice();
            addRecentSearchWithPrevious({
              min_price: null,
              max_price: null,
            });
          })}
          translationKey="price"
        >
          {priceContent}
        </ActiveFilterPill>
      )}
      {captainedIsDirty && (
        <ActiveFilterPill
          clearAction={afterRender(() => {
            clearCaptained();
            addRecentSearchWithPrevious({
              captained: null,
            });
          })}
          translationKey="captain"
        >
          {captainedContent}
        </ActiveFilterPill>
      )}
      {categoriesIsDirty
        && filterCategoriesIds
          .filter((groupCatId) => selectedCategories.includes(groupCatId))
          .map((groupCatId) => (
            <ActiveFilterPill
              key={groupCatId}
              clearAction={afterRender(() => {
                // removes selected category from the list, keeps others
                const categories = selectedCategories.filter((value) => value !== groupCatId);
                onCategoriesChange(categories);
                addRecentSearchWithPrevious({
                  categories,
                });
              })}
              translationKey="category"
            >
              {tBoatCategories(t(getCategoryKeyById(groupCatId)))}
            </ActiveFilterPill>
          ))}
    </>
  );
};

export default ActiveFilters;
